<script setup lang="ts">
import {
  Building2,
  ChevronDown,
  CircleAlert,
  Database,
  HeartPulse,
  Handshake,
  Hotel,
  House,
  KeyRound,
  Languages,
  LogOut,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  Settings,
  ShieldCheck,
  UserRound,
  Users,
  X,
  ListChecks,
  MapPinned,
  MapPin,
  Mail,
  FileText,
  ConciergeBell,
} from '@lucide/vue';
import { useMutation, useQuery } from '@tanstack/vue-query';
import { supportedLocales, type SupportedLocale } from '@travel-crm/types';
import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';
import { RouterLink, RouterView, useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';

import { Toaster } from 'vue-sonner';

import { Button } from '@/components/ui/button';
import { currentUserQueryKey, fetchCurrentUser, logout } from '@/lib/auth';
import { queryClient } from '@/lib/query-client';
import {
  fetchQueryQueueCounts,
  queryListKeys,
  type QueryQueueKey,
} from '@/lib/query-list';
import { usePreferencesStore } from '@/stores/preferences';

const preferences = usePreferencesStore();
const { locale: savedLocale } = storeToRefs(preferences);
const { locale, t } = useI18n();
const route = useRoute();
const router = useRouter();
const isMobileSidebarOpen = ref(false);
const isSidebarCollapsed = ref(false);
const isQueriesPath = (path: string) =>
  path === '/queries' || path.startsWith('/queries/');
const commercialTaxRulesPath =
  '/administration/commercial-tax-rules';

const isMastersPath = (path: string) =>
  path.startsWith('/admin/') ||
  (
    path.startsWith('/administration/') &&
    path !== commercialTaxRulesPath
  ) ||
  path.startsWith('/masters/');

const isAdminPath = (path: string) =>
  path === '/system' ||
  path === commercialTaxRulesPath;

const isQueriesOpen = ref(false);
const isOnHoldOpen = ref(false);
const isConvertedOpen = ref(false);
const isMastersOpen = ref(false);
const isAdminOpen = ref(false);
const currentUser = useQuery({
  queryKey: currentUserQueryKey,
  queryFn: fetchCurrentUser,
  retry: false,
  staleTime: 0,
  enabled: computed(() => route.meta.requiresAuth === true),
});
const logoutMutation = useMutation({
  mutationFn: logout,
  onSuccess: async () => {
    queryClient.removeQueries({ queryKey: currentUserQueryKey });
    await router.replace({ name: 'login' });
  },
});

const can = (permission: string) =>
  currentUser.data.value?.permissions.includes(permission) === true;

const queueCountsQuery = useQuery({
  queryKey: queryListKeys.queueCounts,
  queryFn: fetchQueryQueueCounts,
  retry: false,
  refetchOnWindowFocus: true,
  enabled: computed(() => can('query.view')),
});

const queueCount = (queue: QueryQueueKey): number | undefined =>
  queueCountsQuery.data.value?.data[queue];

const canViewMasters = computed(() =>
  [
    'organization.view',
    'user.view',
    'role.view',
    'permission.view',
    'sales_territory.manage',
    'mailbox.manage',
    'email_template.manage',
    'in_country_location.view',
    'hotel.view',
    'supplier.view',
    'service.view',
    'markup_rule.view',
    'rate_card.import',
    'rate_card.view',
  ].some(can),
);

const userInitials = computed(() => {
  const user = currentUser.data.value;
  if (user === undefined) return '';
  return `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase();
});

const selectedLocale = computed({
  get: () => savedLocale.value,
  set: (value: SupportedLocale) => {
    preferences.setLocale(value);
    locale.value = value;
  },
});

const navigationLinkClass = (path: string) => [
  'group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
  isSidebarCollapsed.value ? 'lg:justify-center lg:px-0' : '',
  route.path === path
    ? 'crm-navigation-active text-foreground shadow-sm'
    : 'text-muted-foreground hover:bg-muted hover:text-foreground',
];

const queryNavLinkClass = (queue: QueryQueueKey) => [
  'group flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
  route.path === '/queries' && route.query.queue === queue
    ? 'crm-navigation-active text-foreground shadow-sm'
    : 'text-muted-foreground hover:bg-muted hover:text-foreground',
];

const toggleQueueGroup = (group: 'QUERIES' | 'ON_HOLD' | 'CONVERTED') => {
  const state = group === 'QUERIES'
    ? isQueriesOpen
    : group === 'ON_HOLD'
      ? isOnHoldOpen
      : isConvertedOpen;
  if (isSidebarCollapsed.value) {
    isSidebarCollapsed.value = false;
    state.value = true;
    return;
  }
  state.value = !state.value;
};

const toggleMasters = () => {
  if (isSidebarCollapsed.value) {
    isSidebarCollapsed.value = false;
    isMastersOpen.value = true;
    return;
  }
  isMastersOpen.value = !isMastersOpen.value;
};

const toggleAdmin = () => {
  if (isSidebarCollapsed.value) {
    isSidebarCollapsed.value = false;
    isAdminOpen.value = true;
    return;
  }
  isAdminOpen.value = !isAdminOpen.value;
};

const handleSidebarNavigation = (event: MouseEvent) => {
  const target = event.target;
  if (target instanceof Element && target.closest('a[href]') !== null) {
    isMobileSidebarOpen.value = false;
  }
};

watch(
  () => currentUser.data.value?.preferredLanguage,
  (preferredLanguage) => {
    if (preferredLanguage === undefined) return;
    preferences.setLocale(preferredLanguage);
    locale.value = preferredLanguage;
  },
  { immediate: true },
);

watch(
  () => [route.path, route.query.queue] as const,
  ([path, queue]) => {
    isMobileSidebarOpen.value = false;
    if (isQueriesPath(path)) {
      if (
        queue === 'PENDING_AVAILABILITY'
        || queue === 'READY_TO_INVOICE'
      ) {
        isOnHoldOpen.value = true;
      } else if (queue === 'PENDING_BOOKINGS') {
        isConvertedOpen.value = true;
      } else {
        isQueriesOpen.value = true;
      }
    } else {
      isQueriesOpen.value = false;
      isOnHoldOpen.value = false;
      isConvertedOpen.value = false;
    }
    isMastersOpen.value = isMastersPath(path);
    isAdminOpen.value = isAdminPath(path);
  },
  { immediate: true },
);
</script>

<template>
  <RouterView v-if="route.name === 'login'" />
  <div
    v-else
    data-testid="sidebar-shell"
    class="crm-app-canvas min-h-screen text-foreground transition-[padding] duration-200"
    :class="isSidebarCollapsed ? 'lg:pl-20' : 'lg:pl-72'"
  >
    <header class="crm-mobile-header sticky top-0 z-30 flex h-16 items-center border-b bg-card/90 px-4 backdrop-blur lg:hidden">
      <button
        type="button"
        data-testid="mobile-sidebar-open"
        class="inline-flex size-10 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        :aria-label="t('navigation.open')"
        @click="isMobileSidebarOpen = true"
      >
        <Menu
          class="size-5"
          aria-hidden="true"
        />
      </button>
      <span class="ml-3 font-semibold tracking-tight">{{ t('appName') }}</span>
    </header>

    <button
      v-if="isMobileSidebarOpen"
      type="button"
      class="fixed inset-0 z-40 bg-foreground/40 lg:hidden"
      :aria-label="t('navigation.close')"
      @click="isMobileSidebarOpen = false"
    />

    <aside
      data-testid="sidebar"
      class="crm-sidebar fixed inset-y-0 left-0 z-50 flex w-72 flex-col border-r shadow-xl transition-[transform,width] duration-200 lg:translate-x-0 lg:shadow-none"
      :class="[
        isMobileSidebarOpen ? 'translate-x-0' : '-translate-x-full',
        isSidebarCollapsed ? 'lg:w-20' : 'lg:w-72',
      ]"
      aria-label="Primary navigation"
    >
      <div
        class="flex h-16 shrink-0 items-center border-b px-5"
        :class="isSidebarCollapsed ? 'lg:px-3' : ''"
      >
        <RouterLink
          to="/"
          class="flex min-w-0 items-center gap-3 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          :class="isSidebarCollapsed ? 'lg:hidden' : ''"
        >
          <span class="crm-brand-mark flex size-9 shrink-0 items-center justify-center rounded-lg text-sm font-bold">T</span>
          <span class="truncate font-semibold tracking-tight">{{ t('appName') }}</span>
        </RouterLink>
        <button
          type="button"
          data-testid="sidebar-collapse"
          class="hidden size-9 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring lg:inline-flex"
          :class="isSidebarCollapsed ? 'mx-auto' : 'ml-auto'"
          :aria-label="isSidebarCollapsed ? t('navigation.expand') : t('navigation.collapse')"
          :title="isSidebarCollapsed ? t('navigation.expand') : t('navigation.collapse')"
          @click="isSidebarCollapsed = !isSidebarCollapsed"
        >
          <PanelLeftOpen
            v-if="isSidebarCollapsed"
            class="size-5"
            aria-hidden="true"
          />
          <PanelLeftClose
            v-else
            class="size-5"
            aria-hidden="true"
          />
        </button>
        <button
          type="button"
          class="ml-auto inline-flex size-9 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring lg:hidden"
          :aria-label="t('navigation.close')"
          @click="isMobileSidebarOpen = false"
        >
          <X
            class="size-5"
            aria-hidden="true"
          />
        </button>
      </div>

      <nav
        class="flex-1 overflow-y-auto px-3 py-5"
        aria-label="Primary navigation links"
        @click="handleSidebarNavigation"
      >
        <div class="space-y-1">
          <RouterLink
            to="/"
            :class="navigationLinkClass('/')"
            :title="isSidebarCollapsed ? t('home') : undefined"
          >
            <House
              class="size-5 shrink-0"
              aria-hidden="true"
            />
            <span :class="isSidebarCollapsed ? 'lg:sr-only' : ''">{{ t('home') }}</span>
          </RouterLink>
          <button
            v-if="can('query.view') && queueCountsQuery.isError.value"
            type="button"
            class="flex w-full items-center gap-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-left text-xs font-medium text-amber-900 hover:bg-amber-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            :class="isSidebarCollapsed ? 'lg:justify-center lg:px-0' : ''"
            :aria-label="t('queryList.retryQueueCounts')"
            :title="isSidebarCollapsed ? t('queryList.queueCountsUnavailable') : undefined"
            data-testid="queue-count-error"
            @click="queueCountsQuery.refetch()"
          >
            <CircleAlert
              class="size-4 shrink-0"
              aria-hidden="true"
            />
            <span :class="isSidebarCollapsed ? 'lg:sr-only' : ''">
              {{ t('queryList.queueCountsUnavailable') }}
            </span>
          </button>
          <div
            v-if="can('query.view')"
            data-testid="queries-menu"
          >
            <button
              type="button"
              class="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              :class="isSidebarCollapsed ? 'lg:justify-center lg:px-0' : ''"
              data-testid="queries-toggle"
              :aria-expanded="isQueriesOpen"
              aria-controls="queries-navigation"
              :title="isSidebarCollapsed ? t('queryList.title') : undefined"
              @click="toggleQueueGroup('QUERIES')"
            >
              <ListChecks
                class="size-5 shrink-0"
                aria-hidden="true"
              />
              <span :class="isSidebarCollapsed ? 'lg:sr-only' : ''">{{ t('queryList.title') }}</span>
              <ChevronDown
                class="ml-auto size-4 transition-transform"
                :class="[
                  isQueriesOpen ? 'rotate-180' : '',
                  isSidebarCollapsed ? 'lg:hidden' : '',
                ]"
                aria-hidden="true"
              />
            </button>
            <div
              v-show="isQueriesOpen"
              id="queries-navigation"
              class="mt-1 ml-5 space-y-1 border-l pl-3"
              :class="isSidebarCollapsed ? 'lg:hidden' : ''"
              :aria-busy="queueCountsQuery.isPending.value"
            >
              <RouterLink
                v-if="can('email.view')"
                to="/queries?queue=PENDING_EMAILS"
                :class="queryNavLinkClass('PENDING_EMAILS')"
                data-testid="queries-nav-pending-emails"
              >
                <span class="size-2 shrink-0 rounded-full bg-amber-500" />
                <span>{{ t('queryList.queues.PENDING_EMAILS') }}</span>
                <span
                  v-if="queueCount('PENDING_EMAILS') !== undefined"
                  class="ml-auto rounded-full bg-muted px-2 py-0.5 text-xs tabular-nums text-foreground"
                  :aria-label="t('queryList.queueCount', {
                    label: t('queryList.queues.PENDING_EMAILS'),
                    count: queueCount('PENDING_EMAILS'),
                  })"
                  data-testid="queue-count-pending-emails"
                >
                  {{ queueCount('PENDING_EMAILS') }}
                </span>
              </RouterLink>
              <RouterLink
                to="/queries?queue=PENDING_ACKNOWLEDGEMENT"
                :class="queryNavLinkClass('PENDING_ACKNOWLEDGEMENT')"
                data-testid="queries-nav-pending-acknowledgement"
              >
                <span class="size-2 shrink-0 rounded-full bg-orange-500" />
                <span>{{ t('queryList.queues.PENDING_ACKNOWLEDGEMENT') }}</span>
                <span
                  v-if="queueCount('PENDING_ACKNOWLEDGEMENT') !== undefined"
                  class="ml-auto rounded-full bg-muted px-2 py-0.5 text-xs tabular-nums text-foreground"
                  :aria-label="t('queryList.queueCount', {
                    label: t('queryList.queues.PENDING_ACKNOWLEDGEMENT'),
                    count: queueCount('PENDING_ACKNOWLEDGEMENT'),
                  })"
                  data-testid="queue-count-pending-acknowledgement"
                >
                  {{ queueCount('PENDING_ACKNOWLEDGEMENT') }}
                </span>
              </RouterLink>
              <RouterLink
                to="/queries?queue=LIVE"
                :class="queryNavLinkClass('LIVE')"
                data-testid="queries-nav-live"
              >
                <span class="size-2 shrink-0 rounded-full bg-emerald-500" />
                <span>{{ t('queryList.queues.LIVE') }}</span>
                <span
                  v-if="queueCount('LIVE') !== undefined"
                  class="ml-auto rounded-full bg-muted px-2 py-0.5 text-xs tabular-nums text-foreground"
                  :aria-label="t('queryList.queueCount', {
                    label: t('queryList.queues.LIVE'),
                    count: queueCount('LIVE'),
                  })"
                  data-testid="queue-count-live"
                >
                  {{ queueCount('LIVE') }}
                </span>
              </RouterLink>
              <RouterLink
                to="/queries?queue=COLD"
                :class="queryNavLinkClass('COLD')"
                data-testid="queries-nav-cold"
              >
                <span class="size-2 shrink-0 rounded-full bg-slate-400" />
                <span>{{ t('queryList.queues.COLD') }}</span>
                <span
                  v-if="queueCount('COLD') !== undefined"
                  class="ml-auto rounded-full bg-muted px-2 py-0.5 text-xs tabular-nums text-foreground"
                  :aria-label="t('queryList.queueCount', {
                    label: t('queryList.queues.COLD'),
                    count: queueCount('COLD'),
                  })"
                  data-testid="queue-count-cold"
                >
                  {{ queueCount('COLD') }}
                </span>
              </RouterLink>
            </div>
          </div>
          <div
            v-if="can('query.view') && can('availability.view')"
            data-testid="on-hold-menu"
          >
            <button
              type="button"
              class="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              :class="isSidebarCollapsed ? 'lg:justify-center lg:px-0' : ''"
              data-testid="on-hold-toggle"
              :aria-expanded="isOnHoldOpen"
              aria-controls="on-hold-navigation"
              :title="isSidebarCollapsed ? 'On Hold' : undefined"
              @click="toggleQueueGroup('ON_HOLD')"
            >
              <HeartPulse
                class="size-5 shrink-0"
                aria-hidden="true"
              />
              <span :class="isSidebarCollapsed ? 'lg:sr-only' : ''">On Hold</span>
              <ChevronDown
                class="ml-auto size-4 transition-transform"
                :class="[
                  isOnHoldOpen ? 'rotate-180' : '',
                  isSidebarCollapsed ? 'lg:hidden' : '',
                ]"
                aria-hidden="true"
              />
            </button>
            <div
              v-show="isOnHoldOpen"
              id="on-hold-navigation"
              class="mt-1 ml-5 space-y-1 border-l pl-3"
              :class="isSidebarCollapsed ? 'lg:hidden' : ''"
            >
              <RouterLink
                to="/queries?queue=PENDING_AVAILABILITY"
                :class="queryNavLinkClass('PENDING_AVAILABILITY')"
                data-testid="queries-nav-pending-availability"
              >
                <span class="size-2 shrink-0 rounded-full bg-yellow-500" />
                <span>{{ t('queryList.queues.PENDING_AVAILABILITY') }}</span>
                <span
                  v-if="queueCount('PENDING_AVAILABILITY') !== undefined"
                  class="ml-auto rounded-full bg-muted px-2 py-0.5 text-xs tabular-nums text-foreground"
                  :aria-label="t('queryList.queueCount', {
                    label: t('queryList.queues.PENDING_AVAILABILITY'),
                    count: queueCount('PENDING_AVAILABILITY'),
                  })"
                  data-testid="queue-count-pending-availability"
                >
                  {{ queueCount('PENDING_AVAILABILITY') }}
                </span>
              </RouterLink>
              <RouterLink
                to="/queries?queue=READY_TO_INVOICE"
                :class="queryNavLinkClass('READY_TO_INVOICE')"
                data-testid="queries-nav-ready-to-invoice"
              >
                <span class="size-2 shrink-0 rounded-full bg-violet-500" />
                <span>{{ t('queryList.queues.READY_TO_INVOICE') }}</span>
                <span
                  v-if="queueCount('READY_TO_INVOICE') !== undefined"
                  class="ml-auto rounded-full bg-muted px-2 py-0.5 text-xs tabular-nums text-foreground"
                  :aria-label="t('queryList.queueCount', {
                    label: t('queryList.queues.READY_TO_INVOICE'),
                    count: queueCount('READY_TO_INVOICE'),
                  })"
                  data-testid="queue-count-ready-to-invoice"
                >
                  {{ queueCount('READY_TO_INVOICE') }}
                </span>
              </RouterLink>
            </div>
          </div>
          <div
            v-if="can('query.view')"
            data-testid="converted-menu"
          >
            <button
              type="button"
              class="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              :class="isSidebarCollapsed ? 'lg:justify-center lg:px-0' : ''"
              data-testid="converted-toggle"
              :aria-expanded="isConvertedOpen"
              aria-controls="converted-navigation"
              :title="isSidebarCollapsed ? 'Converted' : undefined"
              @click="toggleQueueGroup('CONVERTED')"
            >
              <Handshake
                class="size-5 shrink-0"
                aria-hidden="true"
              />
              <span :class="isSidebarCollapsed ? 'lg:sr-only' : ''">Converted</span>
              <ChevronDown
                class="ml-auto size-4 transition-transform"
                :class="[
                  isConvertedOpen ? 'rotate-180' : '',
                  isSidebarCollapsed ? 'lg:hidden' : '',
                ]"
                aria-hidden="true"
              />
            </button>
            <div
              v-show="isConvertedOpen"
              id="converted-navigation"
              class="mt-1 ml-5 space-y-1 border-l pl-3"
              :class="isSidebarCollapsed ? 'lg:hidden' : ''"
            >
              <RouterLink
                to="/queries?queue=PENDING_BOOKINGS"
                :class="queryNavLinkClass('PENDING_BOOKINGS')"
                data-testid="queries-nav-pending-bookings"
              >
                <span class="size-2 shrink-0 rounded-full bg-cyan-500" />
                <span>{{ t('queryList.queues.PENDING_BOOKINGS') }}</span>
                <span
                  v-if="queueCount('PENDING_BOOKINGS') !== undefined"
                  class="ml-auto rounded-full bg-muted px-2 py-0.5 text-xs tabular-nums text-foreground"
                  :aria-label="t('queryList.queueCount', {
                    label: t('queryList.queues.PENDING_BOOKINGS'),
                    count: queueCount('PENDING_BOOKINGS'),
                  })"
                  data-testid="queue-count-pending-bookings"
                >
                  {{ queueCount('PENDING_BOOKINGS') }}
                </span>
              </RouterLink>
            </div>
          </div>
          <RouterLink
            v-if="can('agent.view')"
            to="/agents"
            :class="navigationLinkClass('/agents')"
            :title="isSidebarCollapsed ? t('agents.navigation') : undefined"
          >
            <Users
              class="size-5 shrink-0"
              aria-hidden="true"
            />
            <span :class="isSidebarCollapsed ? 'lg:sr-only' : ''">{{ t('agents.navigation') }}</span>
          </RouterLink>
        </div>

        <div
          v-if="canViewMasters"
          class="mt-5"
          data-testid="masters-menu"
        >
          <button
            type="button"
            class="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            :class="isSidebarCollapsed ? 'lg:justify-center lg:px-0' : ''"
            data-testid="masters-toggle"
            :aria-expanded="isMastersOpen"
            aria-controls="masters-navigation"
            :title="isSidebarCollapsed ? t('navigation.masters') : undefined"
            @click="toggleMasters"
          >
            <Database
              class="size-5 shrink-0"
              aria-hidden="true"
            />
            <span :class="isSidebarCollapsed ? 'lg:sr-only' : ''">{{ t('navigation.masters') }}</span>
            <ChevronDown
              class="ml-auto size-4 transition-transform"
              :class="[
                isMastersOpen ? 'rotate-180' : '',
                isSidebarCollapsed ? 'lg:hidden' : '',
              ]"
              aria-hidden="true"
            />
          </button>
          <div
            v-show="isMastersOpen"
            id="masters-navigation"
            class="mt-1 ml-5 space-y-1 border-l pl-3"
            :class="isSidebarCollapsed ? 'lg:hidden' : ''"
          >
            <RouterLink
              v-if="can('in_country_location.view')"
              to="/masters/in-country-locations"
              :class="navigationLinkClass('/masters/in-country-locations')"
              data-testid="masters-nav-in-country-locations"
            >
              <MapPin
                class="size-4 shrink-0"
                aria-hidden="true"
              />
              <span>{{ t('navigation.inCountryLocations') }}</span>
            </RouterLink>
            <RouterLink
              v-if="can('hotel.view')"
              to="/masters/hotels"
              :class="navigationLinkClass('/masters/hotels')"
              data-testid="masters-nav-hotels"
            >
              <Hotel
                class="size-4 shrink-0"
                aria-hidden="true"
              />
              <span>{{ t('navigation.hotels') }}</span>
            </RouterLink>
            <RouterLink
              v-if="can('supplier.view')"
              to="/masters/suppliers"
              :class="navigationLinkClass('/masters/suppliers')"
              data-testid="masters-nav-suppliers"
            >
              <Handshake
                class="size-4 shrink-0"
                aria-hidden="true"
              />
              <span>{{ t('navigation.suppliers') }}</span>
            </RouterLink>
            <RouterLink
              v-if="can('rate_card.import')"
              to="/masters/rate-imports"
              :class="navigationLinkClass('/masters/rate-imports')"
              data-testid="masters-nav-rate-imports"
            >
              <ListChecks
                class="size-4 shrink-0"
                aria-hidden="true"
              />
              <span>{{ t('rateImports.title') }}</span>
            </RouterLink>
            <RouterLink
              v-if="can('service.view')"
              to="/masters/services"
              :class="navigationLinkClass('/masters/services')"
              data-testid="masters-nav-services"
            >
              <ConciergeBell
                class="size-4 shrink-0"
                aria-hidden="true"
              />
              <span>{{ t('navigation.services') }}</span>
            </RouterLink>
            <RouterLink
              v-if="can('markup_rule.view')"
              to="/masters/markup-rules"
              :class="navigationLinkClass('/masters/markup-rules')"
              data-testid="masters-nav-markup-rules"
            >
              <ListChecks
                class="size-4 shrink-0"
                aria-hidden="true"
              />
              <span>{{ t('markupRules.title') }}</span>
            </RouterLink>
            <RouterLink
              v-if="can('sales_territory.manage')"
              to="/administration/sales-territories"
              :class="navigationLinkClass('/administration/sales-territories')"
            >
              <MapPinned
                class="size-4 shrink-0"
                aria-hidden="true"
              />
              <span>{{ t('salesTerritories.title') }}</span>
            </RouterLink>
            
            <RouterLink
              v-if="can('mailbox.manage')"
              to="/administration/mailboxes"
              :class="navigationLinkClass('/administration/mailboxes')"
            >
              <Mail
                class="size-4 shrink-0"
                aria-hidden="true"
              />
              <span>{{ t('task10.navigation.mailboxes') }}</span>
            </RouterLink>
            <RouterLink
              v-if="can('email_template.manage')"
              to="/administration/email-templates"
              :class="navigationLinkClass('/administration/email-templates')"
            >
              <FileText
                class="size-4 shrink-0"
                aria-hidden="true"
              />
              <span>{{ t('task10.navigation.templates') }}</span>
            </RouterLink>
            <RouterLink
              v-if="can('organization.view')"
              to="/admin/organization"
              :class="navigationLinkClass('/admin/organization')"
            >
              <Building2
                class="size-4 shrink-0"
                aria-hidden="true"
              />
              <span>{{ t('organization.navigation') }}</span>
            </RouterLink>
            <RouterLink
              v-if="can('user.view')"
              to="/admin/users"
              :class="navigationLinkClass('/admin/users')"
            >
              <UserRound
                class="size-4 shrink-0"
                aria-hidden="true"
              />
              <span>{{ t('access.users.navigation') }}</span>
            </RouterLink>
            <RouterLink
              v-if="can('role.view')"
              to="/admin/roles"
              :class="navigationLinkClass('/admin/roles')"
            >
              <ShieldCheck
                class="size-4 shrink-0"
                aria-hidden="true"
              />
              <span>{{ t('access.roles.navigation') }}</span>
            </RouterLink>
            <RouterLink
              v-if="can('permission.view')"
              to="/admin/permissions"
              :class="navigationLinkClass('/admin/permissions')"
            >
              <KeyRound
                class="size-4 shrink-0"
                aria-hidden="true"
              />
              <span>{{ t('access.permissions.navigation') }}</span>
            </RouterLink>
          </div>
        </div>

        <div
          class="mt-5"
          data-testid="admin-menu"
        >
          <button
            type="button"
            class="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            :class="isSidebarCollapsed ? 'lg:justify-center lg:px-0' : ''"
            data-testid="admin-toggle"
            :aria-expanded="isAdminOpen"
            aria-controls="admin-navigation"
            :title="isSidebarCollapsed ? t('navigation.admin') : undefined"
            @click="toggleAdmin"
          >
            <Settings
              class="size-5 shrink-0"
              aria-hidden="true"
            />
            <span :class="isSidebarCollapsed ? 'lg:sr-only' : ''">{{ t('navigation.admin') }}</span>
            <ChevronDown
              class="ml-auto size-4 transition-transform"
              :class="[
                isAdminOpen ? 'rotate-180' : '',
                isSidebarCollapsed ? 'lg:hidden' : '',
              ]"
              aria-hidden="true"
            />
          </button>
          <div
            v-show="isAdminOpen"
            id="admin-navigation"
            class="mt-1 ml-5 border-l pl-3"
            :class="isSidebarCollapsed ? 'lg:hidden' : ''"
          >
            <RouterLink
              v-if="can('commercial_tax_rule.view')"
              to="/administration/commercial-tax-rules"
              :class="
                navigationLinkClass(
                  '/administration/commercial-tax-rules',
                )
              "
              data-testid="administration-nav-commercial-tax-rules"
            >
              <ListChecks
                class="size-4 shrink-0"
                aria-hidden="true"
              />
              <span>{{ t('commercialTaxRules.title') }}</span>
            </RouterLink>
            <RouterLink
              to="/system"
              :class="navigationLinkClass('/system')"
            >
              <HeartPulse
                class="size-4 shrink-0"
                aria-hidden="true"
              />
              <span>{{ t('navigation.health') }}</span>
            </RouterLink>
          </div>
        </div>
      </nav>

      <div
        class="shrink-0 border-t p-3"
        :class="isSidebarCollapsed ? 'lg:p-2' : ''"
        data-testid="profile-section"
      >
        <div
          v-if="currentUser.data.value"
          class="rounded-xl bg-muted/60 p-3"
          :class="isSidebarCollapsed ? 'lg:bg-transparent lg:p-0' : ''"
        >
          <div
            class="flex min-w-0 items-center gap-3"
            :class="isSidebarCollapsed ? 'lg:justify-center' : ''"
          >
            <span class="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary font-semibold text-primary-foreground">
              {{ userInitials }}
            </span>
            <div
              class="min-w-0"
              :class="isSidebarCollapsed ? 'lg:hidden' : ''"
            >
              <p class="truncate text-sm font-semibold">
                {{ currentUser.data.value.firstName }} {{ currentUser.data.value.lastName }}
              </p>
              <p class="truncate text-xs text-muted-foreground">
                {{ currentUser.data.value.email }}
              </p>
            </div>
          </div>
          <label
            class="mt-3 flex items-center gap-2 text-xs font-medium text-muted-foreground"
            :class="isSidebarCollapsed ? 'lg:hidden' : ''"
          >
            <Languages
              class="size-4 shrink-0"
              aria-hidden="true"
            />
            <span class="sr-only">{{ t('language') }}</span>
            <select
              v-model="selectedLocale"
              class="min-w-0 flex-1 rounded-md border bg-card px-2 py-1.5 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              :aria-label="t('language')"
            >
              <option
                v-for="item in supportedLocales"
                :key="item"
                :value="item"
              >{{ item }}</option>
            </select>
          </label>
          <Button
            data-testid="logout-button"
            class="mt-3 w-full gap-2"
            :class="isSidebarCollapsed ? 'lg:size-10 lg:px-0' : ''"
            :disabled="logoutMutation.isPending.value"
            :title="isSidebarCollapsed ? t('auth.logout') : undefined"
            @click="logoutMutation.mutate()"
          >
            <LogOut
              class="size-4"
              aria-hidden="true"
            />
            <span :class="isSidebarCollapsed ? 'lg:sr-only' : ''">{{ t('auth.logout') }}</span>
          </Button>
          <p
            v-if="logoutMutation.isError.value"
            class="mt-2 text-xs leading-5 text-destructive"
            :class="isSidebarCollapsed ? 'lg:hidden' : ''"
            role="alert"
          >
            {{ t('auth.logoutError') }}
          </p>
        </div>
      </div>
    </aside>

    <main class="min-w-0 px-6">
      <RouterView />
    </main>
  </div>

  <Toaster
    rich-colors
    position="top-right"
    :duration="4000"
    close-button
  />
</template>
